import React, { useEffect, useMemo, useState } from 'react';
import { addCrmLeadHubLink, getWarehouses } from '../config/api';

const CrmAddHubDialog = ({ leadId, linkedHubs = [], onClose, onSaved }) => {
  const [hubs, setHubs] = useState([]);
  const [hubId, setHubId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const linkedIds = useMemo(
    () => new Set(linkedHubs.map((hub) => Number(hub.hubId))),
    [linkedHubs]
  );

  useEffect(() => {
    let active = true;
    getWarehouses()
      .then((response) => {
        if (!active) return;
        const data = response?.data;
        const list = Array.isArray(data)
          ? data
          : (data?.warehouses || data?.hubs || []);
        setHubs(list.filter((hub) => hub.active !== false));
      })
      .catch((err) => {
        if (active) setError(err.message || 'Could not load the hub list.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const availableHubs = hubs.filter((hub) => !linkedIds.has(Number(hub.id)));

  const save = async (event) => {
    event.preventDefault();
    if (!hubId || saving) return;
    setSaving(true);
    setError('');
    try {
      await addCrmLeadHubLink(leadId, Number(hubId));
      if (onSaved) await onSaved();
      onClose();
    } catch (err) {
      setError(err.message || 'Could not link the hub.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={saving ? undefined : onClose}>
      <div className="modal-content" onClick={(event) => event.stopPropagation()} style={{ maxWidth: '520px' }}>
        <form onSubmit={save}>
          <div className="modal-header">
            <div>
              <h3>Add hub</h3>
              <div className="crm-modal-subtitle">Link another OMS hub to this Lead.</div>
            </div>
            <button type="button" className="modal-close" onClick={onClose} disabled={saving}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label" htmlFor="crm-lead-hub">Hub</label>
              <select
                id="crm-lead-hub"
                className="form-control"
                value={hubId}
                onChange={(event) => {
                  setHubId(event.target.value);
                  setError('');
                }}
                disabled={loading || saving || availableHubs.length === 0}
                autoFocus
              >
                <option value="">
                  {loading ? 'Loading hubs…' : availableHubs.length ? 'Select a hub' : 'All available hubs are linked'}
                </option>
                {availableHubs.map((hub) => (
                  <option key={hub.id} value={hub.id}>
                    {hub.name}{hub.city ? ` · ${hub.city}` : ''}
                  </option>
                ))}
              </select>
            </div>
            {error && <div className="crm-form-error">{error}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || saving || !hubId}>
              {saving ? 'Adding…' : 'Add hub'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrmAddHubDialog;
